import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Role } from '../proto/generated/identity'
import { FeedbackCategory, SupportConversationStatus, SupportMessage, UserSupportConversationSummary } from '../proto/generated/user-feedback'

export type SupportView = 'home' | 'new' | 'chats' | 'chat' | 'help' | 'shortcuts' | 'contact'

export type SupportChatBack = 'home' | 'chats' | 'form'
export type SupportChatsFrom = 'home' | 'form'

export type SupportThreadCache = {
    messages: SupportMessage[]
    hasOlder: boolean
}

export type SupportPanelStateType = {
    view: SupportView
    conversationId: string | null
    composeCategory: FeedbackCategory | null
    chatBack: SupportChatBack
    chatsFrom: SupportChatsFrom
    unreadConversationIds: string[]
    liveSeq: number
    liveConversationId: string | null
    liveMessageId: string | null
    chats: UserSupportConversationSummary[] | null
    threads: Record<string, SupportThreadCache>
}

const initialState: SupportPanelStateType = {
    view: 'home',
    conversationId: null,
    composeCategory: null,
    chatBack: 'home',
    chatsFrom: 'home',
    unreadConversationIds: [],
    liveSeq: 0,
    liveConversationId: null,
    liveMessageId: null,
    chats: null,
    threads: {}
}

const supportPanelSlice = createSlice({
    name: 'supportPanel',
    initialState,
    reducers: {
        setSupportView(state, action: PayloadAction<SupportView>) {
            state.view = action.payload
        },
        openNewSupportChat(state, action: PayloadAction<FeedbackCategory | null>) {
            state.view = 'new'
            state.conversationId = null
            state.composeCategory = action.payload
            state.chatBack = 'home'
        },
        openSupportChats(state, action: PayloadAction<SupportChatsFrom>) {
            state.view = 'chats'
            state.chatsFrom = action.payload
        },
        openSupportChat(state, action: PayloadAction<{ id: string; back?: SupportChatBack }>) {
            state.view = 'chat'
            state.conversationId = action.payload.id
            state.chatBack = action.payload.back ?? 'chats'
        },
        bindSupportChat(state, action: PayloadAction<string>) {
            state.view = 'chat'
            state.conversationId = action.payload
        },
        noteSupportChatEvent(state, action: PayloadAction<{
            conversationId: string
            messageId: string
            unreadForUser: boolean
            preview: string
            lastMessageAt: number
            status: SupportConversationStatus
            authorRole: Role
        }>) {
            state.liveSeq += 1
            state.liveConversationId = action.payload.conversationId
            state.liveMessageId = action.payload.messageId
            const id = action.payload.conversationId
            if (action.payload.unreadForUser) {
                if (!state.unreadConversationIds.includes(id)) state.unreadConversationIds.push(id)
            } else {
                state.unreadConversationIds = state.unreadConversationIds.filter((item) => item !== id)
            }
            const row = state.chats?.find((item) => item.id === id)
            if (row) {
                row.unreadForUser = action.payload.unreadForUser
                if (action.payload.preview) row.preview = action.payload.preview
                if (action.payload.lastMessageAt) row.lastMessageAt = action.payload.lastMessageAt
                if (action.payload.status) row.status = action.payload.status
                row.lastMessageAuthor = action.payload.authorRole
                state.chats?.sort((a, b) => b.lastMessageAt - a.lastMessageAt)
            }
        },
        setSupportChats(state, action: PayloadAction<UserSupportConversationSummary[]>) {
            state.chats = [...action.payload].sort((a, b) => b.lastMessageAt - a.lastMessageAt)
        },
        upsertSupportChat(state, action: PayloadAction<UserSupportConversationSummary>) {
            if (!state.chats) return
            const row = action.payload
            const index = state.chats.findIndex((item) => item.id === row.id)
            if (index === -1) state.chats.unshift(row)
            else state.chats[index] = row
            state.chats.sort((a, b) => b.lastMessageAt - a.lastMessageAt)
        },
        rememberSupportThread(state, action: PayloadAction<{ id: string; messages: SupportMessage[]; hasOlder: boolean }>) {
            state.threads[action.payload.id] = { messages: action.payload.messages, hasOlder: action.payload.hasOlder }
        },
        setSupportUnreadIds(state, action: PayloadAction<string[]>) {
            state.unreadConversationIds = action.payload
        },
        clearSupportUnread(state, action: PayloadAction<string>) {
            state.unreadConversationIds = state.unreadConversationIds.filter((item) => item !== action.payload)
        }
    }
})

export const {
    setSupportView,
    openNewSupportChat,
    openSupportChats,
    openSupportChat,
    bindSupportChat,
    noteSupportChatEvent,
    setSupportChats,
    upsertSupportChat,
    rememberSupportThread,
    setSupportUnreadIds,
    clearSupportUnread
} = supportPanelSlice.actions

export default supportPanelSlice.reducer
